<template>
  <div class="step-container">
    <!-- Header Section -->
    <StepHeader :currentStep="currentStep" :steps="steps" />

    <!-- Form Section -->
    <div class="form-section">
      <!-- Config Title Field -->
      <div class="field-container">
        <div class="field-header">
          <label class="field-label">{{ lang.W00001 }}</label>
        </div>
        <div class="input-container">
          <input
            v-model="localFormData.title"
            class="text-input"
            :placeholder="lang.W00013"
            :disabled="readonly"
          />
        </div>
        <div class="field-description">
          <p>{{ lang.W00014 }}</p>
        </div>
      </div>

      <div class="form-fields">
        <!-- Project Selection -->
        <div class="field-container">
          <div class="field-header">
            <label class="field-label">{{ lang.W00015 }}</label>
          </div>
          <div class="select-container">
            <select 
              v-if="projects" 
              v-model="localFormData.projectGuid" 
              class="select-input"
              :disabled="readonly"
            >
              <option
                v-for="project in projects"
                :key="project.ProjectGuid"
                :value="project.ProjectGuid"
              >
                {{ project.ProjectName }}
              </option>
            </select>
            <div class="select-arrow">
              <svg class="arrow-icon" fill="none" viewBox="0 0 24 24" stroke="#666666">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 10L12.5 15.5L18 10"></path>
              </svg>
            </div>
          </div>
        </div>

        <!-- Master Survey Selection -->
        <div class="field-container">
          <label class="field-label">{{ lang.W00016 }}</label>
          <div class="select-container">
            <select 
              v-if="projects && surveys" 
              v-model="localFormData.surveyGuid" 
              class="select-input" 
              :disabled="!localFormData.projectGuid || readonly"
            >
              <option
                v-for="survey in filteredSurveys"
                :key="survey.SurveyGuid"
                :value="survey.SurveyGuid"
              >
                {{ survey.SurveyTitle }}
              </option>
            </select>
            <div class="select-arrow">
              <svg class="arrow-icon" fill="none" viewBox="0 0 24 24" stroke="#666666">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 10L12.5 15.5L18 10"></path>
              </svg>
            </div>
          </div>
        </div>

        <!-- Wave Name Field -->
        <div class="field-container">
          <div class="field-header">
            <label class="field-label">{{ lang.W00017 }}</label>
          </div>
          <div class="input-container">
            <input
              v-model="localFormData.waveName"
              class="text-input"
              :placeholder="lang.W00018"
              :disabled="readonly"
            />
          </div>
          <div class="field-description">
            <p>{{ lang.W00019 }}</p>
          </div>
        </div>

        <!-- Wave Start and End -->
        <div class="two-column-grid">
          <div class="field-container">
            <label class="field-label" for="inputWaveStartDate">{{ lang.W00020 }}</label>
            <div class="select-container" @click="globals.toggleFlatpickr('inputWaveStartDate')">
              <input
                type="text"
                class="form-control"
                id="inputWaveStartDate"
                v-model="localFormData.waveSince"
                :disabled="readonly"
              />
            </div>
          </div>

          <div class="field-container">
            <label class="field-label" for="inputWaveEndDate">{{ lang.W00021 }}</label>
            <div class="select-container" @click="globals.toggleFlatpickr('inputWaveEndDate')">
              <input
                type="text"
                class="form-control"
                id="inputWaveEndDate"
                v-model="localFormData.waveUntil"
                :disabled="readonly"
              />
            </div>
          </div>
        </div>

        <!-- Planned Wave Start and End -->
        <div class="two-column-grid">
          <div class="field-container">
            <label class="field-label" for="inputWavePlannedStartDate">{{ lang.W00022 }}</label>
            <div class="select-container" @click="globals.toggleFlatpickr('inputWavePlannedStartDate')">
              <input
                type="text"
                class="form-control"
                id="inputWavePlannedStartDate"
                v-model="localFormData.wavePlannedSince"
                :disabled="readonly"
              />
            </div>
          </div>

          <div class="field-container">
            <label class="field-label" for="inputWavePlannedEndDate">{{ lang.W00023 }}</label>
            <div class="select-container" @click="globals.toggleFlatpickr('inputWavePlannedEndDate')">
              <input
                type="text"
                class="form-control"
                id="inputWavePlannedEndDate"
                v-model="localFormData.wavePlannedUntil"
                :disabled="readonly"
              />
            </div>
          </div>
        </div>
        
        <!-- Wave Info Field -->
        <div class="field-container">
          <div class="field-header">
            <label class="field-label">{{ lang.W00024 }}</label>
          </div>
          <div class="input-container">
            <textarea
              v-model="localFormData.waveInfo"
              class="text-input"
              maxlength="500"
              rows="2"
              :disabled="readonly"
            ></textarea>
          </div>
        </div>

        <div class="field-container">
          <div class="checkbox-container">
            <input
              type="checkbox"
              id="inputWaveWithoutApproval"
              v-model="localFormData.waveWithoutApproval"
              class="checkbox-input"
              :disabled="readonly"
            />
            <label for="inputWaveWithoutApproval" class="checkbox-label">
              {{ lang.W00025 }}
            </label>
          </div>
        </div>

        <div class="field-container">
          <div class="checkbox-container">
            <input
              type="checkbox"
              id="inputIsLocalWave"
              v-model="localFormData.isLocalWave"
              class="checkbox-input"
              :disabled="readonly"
            />
            <label for="inputIsLocalWave" class="checkbox-label">
              {{ lang.W00026 }}
            </label>
          </div>
        </div>

        <!-- Importer Field -->
        <div class="field-container">
          <div class="field-header">
            <label class="field-label">{{ lang.W00027 }}</label>
          </div>
          <div class="select-container">
            <select v-model="localFormData.importerGuid" class="select-input" :disabled="readonly">
              <option
                v-for="importer in importers"
                :key="importer.ImporterGuid"
                :value="importer.ImporterGuid"
              >
                {{ importer.ImporterName }}
              </option>
            </select>
            <div class="select-arrow">
              <svg class="arrow-icon" fill="none" viewBox="0 0 24 24" stroke="#666666">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 10L12.5 15.5L18 10"></path>
              </svg>
            </div>
          </div>
          <div class="field-description">
            <p>{{ lang.W00028 }}</p>
          </div>
        </div>

        <!-- Local Agency -->
        <div class="field-container">
          <div class="field-header">
            <label class="field-label">{{ lang.W00029 }}</label>
          </div>
          <div class="select-container">
            <select v-model="localFormData.agencyGuid" class="select-input" :disabled="readonly">
              <option
                v-for="agency in agencies"
                :key="agency.AgencyGuid"
                :value="agency.AgencyGuid"
              >
                {{ agency.AgencyName }}
              </option>
            </select>
            <div class="select-arrow">
              <svg class="arrow-icon" fill="none" viewBox="0 0 24 24" stroke="#666666">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 10L12.5 15.5L18 10"></path>
              </svg>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import "../../../styles/audi-steps.css";
import StepHeader from './StepHeader.vue';
import wizardService from "../../../services/WizardService";

export default {
  name: 'Step1',
  components: {
    StepHeader
  },
  props: {
    formData: { type: Object, required: true },
    currentStep: { type: Number, default: 0 },
    steps: { type: Array, required: true },
    readonly: { type: Boolean, default: false }
  },
  emits: ['update:formData'],
  data() {
    return {
      localFormData: structuredClone(this.formData),
      projects: null,
      surveys: null,
      agencies: null,
      importers: null,

      fpInputWaveUntil: null,
      fpInputWaveSince: null,
      fpInputWavePlannedUntil: null, 
      fpInputWavePlannedSince : null,

      lang: {
        W00001: "Title",
        W00013: "Enter a title for this configuration",
        W00014: "The title serves to clearly identify the configuration.",
        W00015: "Project",
        W00016: "Survey",
        W00017: "Wave name",
        W00018: "Enter a name for the wave",
        W00019: "The Name serves to clearly identify the wave.",
        W00020: "Start date",
        W00021: "End date",
        W00022: "Planned start date",
        W00023: "Planned end date",
        W00024: "Info (optional)",
        W00025: "Wave without approval",
        W00026: "Local wave",
        W00027: "Importer",
        W00028: "Please note that an DPA must be available for the selected importer.",
        W00029: "Local agency",
      }
    }
  },
  destroyed() {
    if (this.fpInputWaveUntil) this.fpInputWaveUntil.destroy();
    if (this.fpInputWaveSince) this.fpInputWaveSince.destroy();
    if (this.fpInputWavePlannedSince) this.fpInputWavePlannedSince.destroy();
    if (this.fpInputWavePlannedUntil) this.fpInputWavePlannedUntil.destroy();
  },
  computed: {
    filteredSurveys() {
      if(!this.formData.projectGuid) return [];
      if(!this.surveys) return [];
      let project = this.projects.find(e => e.ProjectGuid === this.formData.projectGuid);
      return this.surveys.filter(e => e.ProjectId == project.ProjectId);
    }
  },
  watch: {
    "formData": {
      deep: true,
      handler() {
        this.localFormData = structuredClone(this.formData);
      }
    },
    "localFormData": {
      deep: true,
      handler(newVal) {
        this.$emit('update:formData', newVal);
      }
    },
    "localFormData.surveyGuid"(newVal) {
      if(newVal) {
        let survey = this.surveys.find(e => e.SurveyGuid === newVal);
        if(survey) this.localFormData.surveyId = survey.SurveyId;
      }
    },
    "globals.selectedLanguage"() {
      this.globals.getLanguageStrings(this);
      this.initFlatpickr();
    },
  },
  mounted() {
    if (this.globals.selectedLanguage) {
      this.globals.getLanguageStrings(this);
    }

    this.initFlatpickr();

    this.readMasterSurveysAndProjects();
    this.readAgencies();
    this.readImporters();
  },
  methods: {
    validateStep() {
      let valid = true;
      let msg = null;
      if(!this.formData.title) {
        valid = false;
        msg = "Title is missing.";
      }
      else if(!this.formData.projectGuid) {
        valid = false;
        msg = "Project is missing.";
      }
      else if(!this.formData.surveyGuid) {
        valid = false;
        msg = "Survey is missing.";
      }
      else if(!this.formData.waveName) {
        valid = false;
        msg = "Wave name is missing.";
      }
      else if(!this.formData.waveSince) {
        valid = false;
        msg = "Wave start is missing.";
      }
      else if(!this.formData.waveUntil) {
        valid = false;
        msg = "Wave end is missing.";
      }
      else if(!this.formData.wavePlannedSince) {
        valid = false;
        msg = "Planned wave start is missing.";
      }
      else if(!this.formData.wavePlannedUntil) {
        valid = false;
        msg = "Planned wave end is missing.";
      }
      else if(!this.formData.importerGuid) {
        valid = false;
        msg = "Importer is missing.";
      }
      else if(!this.formData.agencyGuid) {
        valid = false;
        msg = "Agency is missing.";
      }
      let waveSince = new Date(this.formData.waveSince);
      let waveUntil = new Date(this.formData.waveUntil);
      if(waveSince > waveUntil) {
        valid = false;
        msg = "Wave start is after end.";
      }
      let wavePlannedSince = new Date(this.formData.wavePlannedSince);
      let wavePlannedUntil = new Date(this.formData.wavePlannedUntil);
      if(wavePlannedSince > wavePlannedUntil) {
        valid = false;
        msg = "Planned wave start is after end.";
      }
      
      return { "valid": valid, "msg": msg};
    },
    initFlatpickr() {
      this.fpInputWaveSince = flatpickr(
        "#inputWaveStartDate",
        this.globals.flatpickrDateConfig
      );
      this.fpInputWaveUntil = flatpickr(
        "#inputWaveEndDate",
        this.globals.flatpickrDateConfig
      );
      this.fpInputWavePlannedSince = flatpickr(
        "#inputWavePlannedStartDate",
        this.globals.flatpickrDateConfig
      );
      this.fpInputWavePlannedUntil = flatpickr(
        "#inputWavePlannedEndDate",
        this.globals.flatpickrDateConfig
      );
      
      this.setDatePicker();
    },
    async readMasterSurveysAndProjects() {
      let res = await wizardService.readMasterSurveysAndProjects();
      if(res.Surveys) this.surveys = res.Surveys;
      if(res.Projects) this.projects = res.Projects;
    },
    async readAgencies() {
      let res = await wizardService.readAgencies();
      if(res.length && res.length > 0) this.agencies = res;
    },
    async readImporters() {
      let res = await wizardService.readImporters();
      if(res.length && res.length > 0) this.importers = res;
    },      
    setDatePicker(){    
      this.fpInputWaveSince.setDate(this.formData.waveSince);
      this.fpInputWaveUntil.setDate(this.formData.waveUntil);
      this.fpInputWavePlannedSince.setDate(this.formData.wavePlannedSince);
      this.fpInputWavePlannedUntil.setDate(this.formData.wavePlannedUntil); 
    }
  }
}
</script>

<style lang="css" scoped>

.form-section {
  max-width: 1536px;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 1024px) {
    gap: 16px;
  }
}

.field-container {
  width: 100%;
}

.two-column-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
}

.field-header {
  padding: 16px 0 8px 0;
  text-align: left;
  font-weight: 600;

  @media (min-width: 1024px) {
    padding: 16px 0 8px 0;
  }
}

.field-label {
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #333333;
  margin-bottom: 8px;
  text-align: left;

  @media (min-width: 1024px) {
    font-size: 16px;
  }
}

.input-container {
  position: relative;
}

.text-input {
  width: 100%;
  border: 0;
  border-bottom: 2px solid #e0e0e0;
  border-radius: 0;
  background: transparent;
  font-size: 16px;
  font-weight: normal;
  color: #333333;
  padding: 12px 0;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-bottom-color: var(--color-primary);
  }

  &::placeholder {
    color: #999999;
  }
  &:disabled {
    color: gray;
    cursor: not-allowed;
  }
}

.select-container {
  position: relative;
}

.select-input {
  width: 100%;
  border: 0;
  border-bottom: 2px solid #e0e0e0;
  border-radius: 0;
  background: transparent;
  font-size: 16px;
  font-weight: normal;
  color: #333333;
  padding: 12px 32px 12px 0;
  appearance: none;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-bottom-color: var(--color-primary);
  }
}
.select-input:disabled {
  /* background: rgba(0, 0, 0, 0.150); */
  cursor: not-allowed;
}

.select-arrow {
  position: absolute;
  right: 0;
  top: 12px;
  pointer-events: none;
}

.arrow-icon {
  width: 16px;
  height: 16px;
}

.field-description {
  padding-top: 12px;
  max-width: 512px;
  text-align: left;

  p {
    font-size: 14px;
    color: #666666;
  }
}

.checkbox-container {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.checkbox-input {
  width: 16px;
  height: 16px;
  margin: 0 !important;
}

.checkbox-label {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  color: #333333;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// Step1.vue?11c0c5e3